import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApiMutation } from '../../../../hooks/useApi';
import { personaService } from '../../../../services/personaService';
import type {
  AIGeneratePersonaRequest,
  AIGeneratedPersona,
  AudiencePersona,
  CreatePersonaPayload,
} from '../../../../services/personaService';
import { useCurrentWorkspaceId } from '../../../../hooks/useStoreSelectors';
import { useToast } from '../../../../hooks/useToast';
import { ROUTES } from '../../../../routes/routeConfig';

interface UseAIPersonaGenerationReturn {
  /** Trigger AI generation with the provided overview */
  generate: (request: AIGeneratePersonaRequest) => void;
  /** The AI-generated persona data (preview before saving) */
  generatedPersona: AIGeneratedPersona | null;
  /** Whether the AI is currently generating */
  isGenerating: boolean;
  /** Error message if generation failed */
  generationError: string | null;
  /** Save the generated persona to the database */
  savePersona: () => void;
  /** Whether save is in progress */
  isSaving: boolean;
  /** Reset the flow to start over */
  reset: () => void;
}

export function useAIPersonaGeneration(): UseAIPersonaGenerationReturn {
  const workspaceId = useCurrentWorkspaceId();
  const navigate = useNavigate();
  const toast = useToast();
  const [generatedPersona, setGeneratedPersona] = useState<AIGeneratedPersona | null>(null);
  const [generationError, setGenerationError] = useState<string | null>(null);

  // Generate mutation
  const generateMutation = useApiMutation<AIGeneratedPersona, AIGeneratePersonaRequest>({
    mutationFn: (request: AIGeneratePersonaRequest) =>
      personaService.aiGenerate(workspaceId!, request),
    errorMessage: 'Failed to generate persona.',
  });

  // Save mutation
  const saveMutation = useApiMutation<AudiencePersona, CreatePersonaPayload>({
    mutationFn: (payload: CreatePersonaPayload) =>
      personaService.create(workspaceId!, payload),
    successMessage: 'Persona created successfully.',
    errorMessage: 'Failed to save persona.',
    invalidateKeys: [['personas', workspaceId]],
  });

  const generate = useCallback(
    (request: AIGeneratePersonaRequest) => {
      if (!workspaceId) {
        toast.error('Error', 'No workspace selected.');
        return;
      }

      setGenerationError(null);
      setGeneratedPersona(null);

      generateMutation.mutate(request, {
        onSuccess: (data) => {
          setGeneratedPersona(data);
        },
        onError: (error: any) => {
          const message =
            error?.response?.data?.message || error?.message || 'Failed to generate persona.';
          setGenerationError(message);
        },
      });
    },
    [workspaceId, generateMutation, toast]
  );

  const savePersona = useCallback(() => {
    if (!generatedPersona || !workspaceId) return;

    const payload: CreatePersonaPayload = {
      name: generatedPersona.name,
      description: generatedPersona.description,
      demographics: generatedPersona.demographics,
      interests: generatedPersona.interests,
      painPoints: generatedPersona.painPoints,
      goals: generatedPersona.goals,
      funnelStage: generatedPersona.funnelStage,
      preferredChannels: generatedPersona.preferredChannels,
      contentPreferences: generatedPersona.contentPreferences,
      tonePreferences: generatedPersona.tonePreferences,
    };

    saveMutation.mutate(payload, {
      onSuccess: () => {
        navigate(ROUTES.DASHBOARD_PERSONAS);
      },
    });
  }, [generatedPersona, workspaceId, saveMutation, navigate]);

  const reset = useCallback(() => {
    setGeneratedPersona(null);
    setGenerationError(null);
  }, []);

  return {
    generate,
    generatedPersona,
    isGenerating: generateMutation.isPending,
    generationError,
    savePersona,
    isSaving: saveMutation.isPending,
    reset,
  };
}